import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-1 mt-2 px-4 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 first:mt-0">
    {children}
  </div>
);

export const KindBadge = ({ label }: { label: string }) => (
  <span className="hidden rounded-md bg-white/[0.04] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-slate-400 sm:inline-block">
    {label}
  </span>
);

// Modified / non-primary clicks belong to the browser (new tab, new window…).
const isPlainClick = (e: React.MouseEvent) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

type Props = {
  href: string;
  idx: number;
  active: boolean;
  onNavigate: () => void;
  onHover: () => void;
  children: React.ReactNode;
};

/**
 * A result is a real link, so the browser's own gestures work (⌘/Ctrl+click,
 * middle click, right click → "Open in new tab"); a plain click stays an
 * in-app navigation. The trailing icon always opens it in a new tab.
 */
const ResultRow = ({ href, idx, active, onNavigate, onHover, children }: Props) => (
  <div
    data-idx={idx}
    onMouseEnter={onHover}
    className={cn(
      "relative flex w-full items-center transition",
      active ? "bg-gradient-to-r from-brand-500/10 to-transparent" : "hover:bg-white/[0.02]"
    )}
  >
    {active && (
      <span className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full bg-gradient-to-b from-brand-300 to-brand-500 shadow-[0_0_12px_rgba(59,130,246,0.7)]" />
    )}
    <a
      href={href}
      onClick={(e) => {
        if (!isPlainClick(e)) return;
        e.preventDefault();
        onNavigate();
      }}
      className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-4 pr-2 text-left focus:outline-none"
    >
      {children}
    </a>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Open in new tab"
      title="Open in new tab"
      className={cn(
        "mr-2.5 grid h-7 w-7 shrink-0 place-items-center rounded-md transition hover:bg-white/[0.06] hover:text-white",
        active ? "text-brand-200" : "text-slate-600"
      )}
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  </div>
);

export default ResultRow;
